import { defineStore } from 'pinia';
import { Schedule } from '@/entities/schedule';
import { emptyReminder, useScheduleRecord } from '@/ui/pages/scheduleManager/useScheduleRecord';
import { useRRule } from '@/ui/concerns/rrule/useRRule';
import { differenceInMinutes } from 'date-fns';
import { Days, Repeat } from '@/entities/rrule/constants';
import { separateUsersAndGroups } from '@/ui/helpers';

interface State {
  id?: null | number;
  record: Schedule;
  version: string;
}

const {
  emptyMonthly,
  emptyWeekly,
} = useRRule();

export const useScheduleStore = defineStore('schedule', {
  state: (): State => ({
    id: null,
    record: useScheduleRecord(),
    version: '',
  }),
  actions: {
    setId(id: number) {
      this.id = id;
    },
    setRecord(data: Schedule) {
      this.record = data;
    },
    setVersion(data: string) {
      this.version = data;
    },
    updateTitle(value: string) {
      this.record.title = value;
    },
    updateChecklists(value: number[]) {
      this.record.checklists = value;
    },
    updateAssignees(value: string[]) {
      const { users, groups } = separateUsersAndGroups(value);

      this.record.assignees = users;
      this.record.assignedGroups = groups;
    },
    updateSeparateTodoValue(value: boolean) {
      this.record.separateTodoForAllUsers = value;
    },
    setRepeat(value: string) {
      this.record.rule.repeat = value;

      if (this.record.rule.repeat !== Repeat.Custom) {
        return;
      }

      if (!this.record.rule.frequency) {
        this.record.rule.frequency = Repeat.Weekly;
        this.record.rule.weekly = emptyWeekly();
      }
    },
    setFrequency(value: Repeat) {
      this.record.rule.frequency = value;

      if (value === Repeat.Weekly && !this.record.rule.weekly) {
        this.record.rule.weekly = emptyWeekly();
      }

      if (value === Repeat.Monthly && !this.record.rule.monthly) {
        this.record.rule.monthly = emptyMonthly();
      }
    },
    addReminder() {
      this.record.reminders.push(emptyReminder());
    },
    deleteReminder(index: number) {
      this.record.reminders.splice(index, 1);
    },
    updateSupervisors(value: string[]) {
      const { users, groups } = separateUsersAndGroups(value);

      this.record.supervisors = users;
      this.record.supervisorGroups = groups;
    },
    updateRuleStart(value: Date) {
      this.record.rule.start = value;
    },
    handleDueAtChange(value: Date) {
      this.record.dueIn = differenceInMinutes(value, this.record.rule.start!);
    },
    updateRuleInterval(value: number) {
      this.record.rule.interval = value;
    },
    changeDay(value: boolean, day: Days) {
      if (!this.record.rule.weekly) {
        this.record.rule.weekly = emptyWeekly();
      }

      this.record.rule.weekly[day] = value;
    },
    updateMonthDaysFrequency(value: string) {
      this.record.rule.monthly!.day = value;
    },
    updateMonthPosition(value: number) {
      this.record.rule.monthly!.setpos = value;
    },
    updateEntities(value: number[]) {
      if (this.record.allEntityItems) {
        this.record.allEntityItems = false;
      }

      this.record.entities = value;
    },
    setAllEntities() {
      this.record.allEntityItems = true;
      this.record.entities = [];
    },
    resetAllEntities() {
      this.record.allEntityItems = false;
      this.record.entities = [];
    },
    updateEntityItemSpecificAssignments(value: boolean) {
      this.record.entityItemSpecificAssignments = value;
    },
    updateReminderValue(value: number, index: number) {
      this.record.reminders[index].value = value;
    },
    updateReminderScale(value: string, index: number) {
      this.record.reminders[index].scale = value;
    },
    updateReminderDirection(value: string, index: number) {
      this.record.reminders[index].direction = value;
    },
    updateReminderType(value: string, index: number) {
      this.record.reminders[index].type = value;
    },
  },
});
